import { BreakpointObserver } from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { Router } from '@angular/router';
import { Cms, TempAny } from '@impact/data';
import { Environment, ENVIRONMENT } from '@impact/ui';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SettingsService } from '../core/settings.service';
import { DealershipDepartments } from '../models/dealerships';
import { FeatureDetectionService } from '../utils/helpers/feature-detection.service';
import { filterLocationsByShowOn } from './filter-location-by-show-on.helper';
import { MapsService } from './maps.service';

export interface ICoordinations {
    lat: number;
    lng: number;
}

export interface MapsLocation extends Cms.DealershipDto {
    pageUrl: string;
}

export enum MapView {
    DEALERSHIPS_WITH_MAKE = 'dealerships-with-make',
    ALL_DEALERSHIPS = 'all-dealerships',
    ONE_DEALERSHIP = 'one-dealership',
}

export enum MapLayout {
    LIST = 'list-layout',
    MAP = 'map-layout',
    BOTH = 'columns-layout',
}

@Component({
    selector: 'app-maps',
    template: `
        <div appInViewport (intersectOutput)="intersectInitComponent()">
            <div
                class="maps"
                [ngClass]="{
                    'maps--list-view': activeView === mapLayout.LIST,
                    'maps--map-view': activeView === mapLayout.MAP,
                    'maps--full-view': activeView === mapLayout.BOTH
                }"
            >
                <app-section-header
                    *ngIf="mapView === mapViewEnum.DEALERSHIPS_WITH_MAKE"
                    [headerTextPrimary]="'dealerships.map_header_brand' | translate: { makeName: vehicleMakeName }"
                ></app-section-header>

                <app-section-header
                    *ngIf="mapView === mapViewEnum.ONE_DEALERSHIP"
                    [headerTextPrimary]="'dealerships.map_header_specific' | translate: { dealershipName: locations[0]?.displayName || '' }"
                ></app-section-header>

                <app-section-header
                    *ngIf="mapView === mapViewEnum.ALL_DEALERSHIPS && isMotorpoint"
                    [headerTextPrimary]="'dealerships.map_header_showroom' | translate"
                ></app-section-header>

                <app-section-header
                    *ngIf="mapView === mapViewEnum.ALL_DEALERSHIPS && !isMotorpoint"
                    [headerTextPrimary]="'dealerships.map_header_all' | translate"
                ></app-section-header>

                <ul role="navigation" class="maps__tabs-navigation">
                    <li>
                        <button
                            class="button button--minimal maps__tabs-navigation-button"
                            [ngClass]="{
                                'maps__tabs-navigation-button--is-active': activeView === mapLayout.LIST
                            }"
                            (click)="toggleMapsView(mapLayout.LIST)"
                        >
                            {{ 'buttons.map_button_list_view' | translate }}
                        </button>
                    </li>
                    <li>
                        <button
                            class="button button--minimal maps__tabs-navigation-button"
                            [ngClass]="{
                                'maps__tabs-navigation-button--is-active': activeView === mapLayout.MAP
                            }"
                            (click)="toggleMapsView(mapLayout.MAP)"
                        >
                            {{ 'buttons.map_button_map_view' | translate }}
                        </button>
                    </li>
                </ul>

                <div class="maps__content">
                    <div class="maps__locations">
                        <!-- LOCATIONS LIST -->
                        <div class="maps__locations-list-wrapper">
                            <ul class="maps__locations-list" #listLocationElements>
                                <li
                                    class="maps__locations-list-item"
                                    #listLocationElement
                                    *ngFor="let location of locations"
                                    [ngClass]="{ 'maps__locations-list-item--is-active': activeListItem?.id === location?.id }"
                                >
                                    <button type="button" class="button--minimal maps__locations-list-button" (click)="listItemClick(location)">
                                        <div class="location__info">
                                            <h3 class="h5 location__header">{{ location?.displayName }}</h3>
                                            <address class="location__address">{{ location?.address }}</address>
                                        </div>
                                        <ul class="dealership-brands__list">
                                            <ng-container *ngFor="let make of location?.vehicleMakes">
                                                <li class="dealership-brands__item" *ngIf="make?.logo?.src">
                                                    <img
                                                        [src]="make?.logo?.src | resolveMediaUrl: { format: 'webp', width: 60, height: 22, rmode: 'max' }"
                                                        [alt]="make?.logo?.alt"
                                                        loading="lazy"
                                                    />
                                                </li>
                                            </ng-container>
                                        </ul>
                                    </button>
                                    <a [routerLink]="location?.pageUrl" class="dealership__details-link" *ngIf="location?.pageUrl">
                                        {{ 'dealerships.dealerships_map_list_opening_hours_text' | translate }}
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <!-- MAP -->
                        <div class="maps__locations-map" *ngIf="showMap && isMapScriptLoaded">
                            <google-map
                                class="map"
                                height="100%"
                                width="100%"
                                [center]="center"
                                [zoom]="zoom"
                                [options]="{ maxZoom: 16 }"
                                (mapClick)="closeInfo()"
                                *ngIf="isMapScriptLoaded && locations"
                            >
                                <map-marker
                                    #marker="mapMarker"
                                    *ngFor="let markerPosition of markerPositions"
                                    [position]="markerPosition.position"
                                    [options]="markerOptions"
                                    (mapClick)="markerClick(marker, markerPosition.location)"
                                >
                                </map-marker>
                            </google-map>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapsComponent implements OnInit, OnDestroy {
    @ViewChild(GoogleMap) map: GoogleMap;
    @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
    @ViewChildren('marker') markers: QueryList<MapMarker>;
    @ViewChildren('listLocationElement') listLocationElements: QueryList<ElementRef>;

    // The choice of inputs controls what data is displayed in the list and map
    @Input() mapView: MapView;
    @Input() vehicleMakeName?: string;
    @Input() locations: MapsLocation[] = [];

    private readonly unsubscribe = new Subject<void>();
    mapLayout = MapLayout;
    mapViewEnum = MapView;
    dealershipDepartments = DealershipDepartments;
    salesDepartment: Cms.DepartmentDto;
    openingHoursDepartmentName: string;
    openingHoursData: Cms.OpeningHourDto[] | undefined = [];

    markerPinAssetsUrl = 'assets/icons/bnMarkerPin.svg';
    showingAllDealerships = false;
    dealershipsOverviewPage: Cms.Link;
    currentLocation?: MapsLocation;
    regionCode = 'dk';
    languageCode = 'da';
    showMap = false;
    isMapScriptLoaded = false;
    activeView: string;
    activeListItem: MapsLocation;
    isBrowser = false;
    isMotorpoint = this.environment.site === 'Motorpoint';

    // Map properties
    bounds: google.maps.LatLngBounds;
    center = { lat: 56.19612466324578, lng: 10.137457154410324 };
    markerOptions: google.maps.MarkerOptions;
    markerPositions: { position: google.maps.LatLngLiteral; location: MapsLocation }[] = [];
    markerZoomLevel = 16;
    zoom = 7;
    searchRadius = 28; // In km

    constructor(
        private mapService: MapsService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private featureDetectionService: FeatureDetectionService,
        private settingsService: SettingsService,
        private breakpointObserver: BreakpointObserver,
        @Inject(ENVIRONMENT) private readonly environment: Environment
    ) {
        this.isBrowser = featureDetectionService.isBrowser();
        this.settingsService.getGlobalPages().subscribe((data) => {
            this.dealershipsOverviewPage = data.dealershipsOverviewPage;
        });
    }

    // Will init when intersection observer directive triggers it
    async intersectInitComponent() {
        if (this.featureDetectionService.isBrowser()) {
            this.mapService.initGoogleMapsScripts();

            this.isMapScriptLoaded = await this.mapService.isMapScriptsReady;

            if (this.isMapScriptLoaded) {
                this.showMap = true;
                this.initMap();
            }
        }
    }

    ngOnInit() {
        if (!this.locations) {
            console.log('[BN Maps] no locations found');
            return;
        }

        // Detect layout changes
        const layoutChanges$ = this.breakpointObserver.observe(['(min-width: 900px)']);

        layoutChanges$.pipe(takeUntil(this.unsubscribe)).subscribe((result) => {
            if (result.matches) {
                this.activeView = MapLayout.BOTH;
            } else {
                this.activeView = MapLayout.LIST;
            }

            this.cdr.markForCheck();
        });
    }

    initMap() {
        if (this.locations?.length) {
            this.locations = this.locations
                .filter((location) => filterLocationsByShowOn(location, this.environment.site))
                .sort((a, b) => a.displayName.localeCompare(b.displayName));

            if (this.isBrowser) {
                this.setDefaultMapOptions();
                this.setMarkers();
                this.showMap = true;
            }
            this.cdr.detectChanges();
        }

        // Fit map to bounds
        if (this.map && this.bounds) {
            if (this.mapView === MapView.ONE_DEALERSHIP && this.currentLocation) {
                this.center = {
                    lat: Number(this.currentLocation?.locationLatitude),
                    lng: Number(this.currentLocation?.locationLongitude),
                };
                this.zoom = 12;
            } else {
                this.map.fitBounds(this.bounds);
                this.map.panToBounds(this.bounds);
            }
        }

        // Set Timeout is necessary for zoom to update
        const currentLocationTimeout = setTimeout(() => {
            // Activate the only location on map
            if (this.locations?.length === 1 && this.markerPositions[0]) {
                this.currentLocation = this.markerPositions[0].location;
            }

            clearTimeout(currentLocationTimeout);

            this.cdr.detectChanges();
        }, 0);
    }

    toggleMapsView(view: string): void {
        this.activeView = view;

        const activeViewTimeout = setTimeout(() => {
            // Activate the only location on map
            if (this.activeView === MapLayout.MAP && !this.currentLocation) {
                if (this.locations?.length === 1 && this.markerPositions[0]) {
                    this.currentLocation = this.markerPositions[0].location;
                    this.listItemClick(this.currentLocation);
                } else {
                    this.map.fitBounds(this.bounds);
                    this.map.panToBounds(this.bounds);
                    this.cdr.detectChanges();
                }
            }

            clearTimeout(activeViewTimeout);

            this.cdr.detectChanges();
        }, 0);
    }

    addMarker(location: TempAny) {
        const position = new google.maps.LatLng(Number(location.locationLatitude), Number(location.locationLongitude));

        this.markerPositions.push({
            position: {
                lat: position.lat(),
                lng: position.lng(),
            },
            location,
        });

        // Sorting of markers
        this.markerPositions.sort((a, b) =>
            a.position.lat - b.position.lat || a.position.lng - b.position.lng
        );
        this.bounds = this.markerPositions.reduce((bounds, marker) => bounds.extend(marker.position), new google.maps.LatLngBounds());

    }

    setDefaultMapOptions() {
        this.bounds = new google.maps.LatLngBounds();
        this.setMarkerOptions();
    }

    setMarkerOptions() {
        this.markerOptions = {
            draggable: false,
            icon: this.markerPinAssetsUrl,
            animation: google.maps.Animation.DROP,
        };
    }

    setMarkers() {
        this.locations.forEach((location) => {
            this.addMarker(location);
        });
    }

    markerClick(marker: MapMarker, location: MapsLocation) {
        if (location?.pageUrl) {
            this.router.navigateByUrl(location.pageUrl);
        }
    }

    listItemClick(item: MapsLocation, showMap?: boolean) {
        if (showMap) {
            this.activeView = MapLayout.MAP;
        }

        const index = this.markerPositions.findIndex((position) => {
            return position.location.id === item.id;
        });

        if (index) {
            this.scrollToListItem(index);
        }

        this.activeListItem = item;

        const marker = this.markers.toArray()[index];
        this.panAndZoomToMarker(marker);
        this.cdr.detectChanges();
    }

    panAndZoomToMarker(marker: MapMarker) {
        const position = marker.getPosition();
        if (position) {
            this.map.panTo(position);
            this.map.zoom = this.markerZoomLevel;
        }
        this.cdr.markForCheck();
    }

    scrollToListItem(index: number): void {
        if (this.featureDetectionService.isBrowser()) {
            const element: HTMLElement = this.listLocationElements.toArray()[index].nativeElement;
            const parentElement = element.parentElement;

            if (parentElement) {
                const scrollDistance = element.offsetTop - parentElement?.scrollTop - parentElement.clientHeight / 2 + element.clientHeight;
                parentElement?.scrollBy({
                    top: scrollDistance,
                    left: 0,
                    behavior: 'smooth',
                });
            }
        }
    }

    closeInfo() {
        if (this.infoWindow) {
            this.infoWindow.close();
            this.currentLocation = undefined;
            this.cdr.markForCheck();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
